import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { X, Loader2, Send, RotateCcw, Square, Maximize2, Crop as CropIcon, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * WhatsApp-style image preview + optional cropper.
 * Flow:
 *   1. mode="preview" → shows full image; user can tap "Ritaglia" to enter crop, or "Invia" to send as-is.
 *   2. mode="crop"    → shows react-easy-crop editor. "Applica" bakes the crop and returns to preview.
 * Cancel (X) always aborts and calls onCancel().
 */
async function getCroppedBlob(imageSrc, cropPixels, rotation = 0) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const rotRad = (rotation * Math.PI) / 180;
  const canvas = document.createElement('canvas');
  const safeArea = Math.max(image.width, image.height) * 2;
  canvas.width = safeArea;
  canvas.height = safeArea;
  const ctx = canvas.getContext('2d');
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - cropPixels.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - cropPixels.y),
  );
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
}

async function blobToDataUrl(blob) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(blob);
  });
}

export default function ChatImageCropper({ imageSrc, onCancel, onConfirm }) {
  const [mode, setMode] = useState('preview'); // 'preview' | 'crop'
  // The image currently shown in preview (original at first; replaced by cropped version if user applies crop)
  const [previewSrc, setPreviewSrc] = useState(imageSrc);
  // Whether the current previewSrc is already a cropped blob (jpeg)
  const [hasCropped, setHasCropped] = useState(false);
  // Sending state (upload)
  const [sending, setSending] = useState(false);

  // Crop-mode local state — reset every time we (re)enter crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(undefined); // undefined = free
  const [cropPixels, setCropPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => { setPreviewSrc(imageSrc); setHasCropped(false); }, [imageSrc]);

  const onCropComplete = useCallback((_, pixels) => setCropPixels(pixels), []);

  const enterCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAspect(undefined);
    setCropPixels(null);
    setMode('crop');
  };

  const applyCrop = async () => {
    if (!cropPixels) { setMode('preview'); return; }
    setApplying(true);
    try {
      const blob = await getCroppedBlob(previewSrc, cropPixels, rotation);
      const dataUrl = await blobToDataUrl(blob);
      setPreviewSrc(dataUrl);
      setHasCropped(true);
      setMode('preview');
    } catch (e) {
      toast.error('Errore durante il ritaglio');
    } finally {
      setApplying(false);
    }
  };

  const cancelCropMode = () => setMode('preview');

  const sendCurrent = async () => {
    setSending(true);
    try {
      let blob;
      if (hasCropped) {
        // previewSrc is already a data URL of a jpeg blob → convert back to Blob
        const res = await fetch(previewSrc);
        blob = await res.blob();
      } else {
        // original image (object URL): fetch and send as-is
        const res = await fetch(imageSrc);
        blob = await res.blob();
      }
      await onConfirm(blob);
    } catch (e) {
      toast.error('Invio immagine fallito');
    } finally {
      setSending(false);
    }
  };

  // -------- Render: PREVIEW mode ----------
  if (mode === 'preview') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9998] bg-black flex flex-col"
        data-testid="chat-image-preview"
      >
        <div className="flex items-center justify-between p-3 safe-top">
          <button onClick={onCancel} className="p-2 text-white" data-testid="preview-cancel" aria-label="Annulla">
            <X className="w-5 h-5" />
          </button>
          <div className="text-white/80 text-sm font-heading">
            Anteprima immagine{hasCropped ? ' · ritagliata' : ''}
          </div>
          <div className="w-9" />
        </div>

        <div className="relative flex-1 flex items-center justify-center overflow-hidden">
          <img
            src={previewSrc}
            alt="anteprima"
            className="max-w-full max-h-full object-contain select-none pointer-events-none"
            data-testid="preview-image"
          />
        </div>

        <div className="p-3 safe-bottom bg-black/80 flex items-center gap-2">
          <button
            onClick={enterCrop}
            className="flex-1 h-11 rounded-full border border-white/20 text-white text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition"
            data-testid="preview-crop-btn"
          >
            <CropIcon className="w-4 h-4" />
            {hasCropped ? 'Ritaglia di nuovo' : 'Ritaglia'}
          </button>
          <button
            onClick={sendCurrent}
            disabled={sending}
            className="flex-1 h-11 rounded-full bg-[var(--gold)] text-black text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition"
            data-testid="preview-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Invia</>}
          </button>
        </div>
      </motion.div>
    );
  }

  // -------- Render: CROP mode ----------
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9998] bg-black flex flex-col"
      data-testid="chat-image-cropper"
    >
      <div className="flex items-center justify-between p-3 safe-top">
        <button onClick={cancelCropMode} className="p-2 text-white" data-testid="crop-back" aria-label="Indietro">
          <X className="w-5 h-5" />
        </button>
        <div className="text-white/80 text-sm font-heading">Ritaglia immagine</div>
        <button
          onClick={applyCrop}
          disabled={applying || !cropPixels}
          data-testid="crop-apply"
          className="px-3 py-1.5 rounded-full bg-[var(--gold)] text-black text-xs font-bold disabled:opacity-60 flex items-center gap-1"
        >
          {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Applica</>}
        </button>
      </div>

      <div className="relative flex-1">
        <Cropper
          image={previewSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          objectFit="contain"
          restrictPosition={false}
        />
      </div>

      <div className="p-3 safe-bottom space-y-3 bg-black/80">
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-white/60 uppercase tracking-widest w-14">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[var(--gold)]"
            data-testid="crop-zoom"
          />
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
            className="px-3 py-2 rounded-full border border-white/15 text-white/80 text-xs flex items-center gap-1"
            data-testid="crop-rotate"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Ruota
          </button>
          <button
            onClick={() => setAspect(aspect === 1 ? undefined : 1)}
            className={`px-3 py-2 rounded-full border text-xs flex items-center gap-1 ${aspect === 1 ? 'bg-[var(--gold)] text-black border-[var(--gold)]' : 'border-white/15 text-white/80'}`}
            data-testid="crop-square"
          >
            <Square className="w-3.5 h-3.5" /> Quadrato
          </button>
          <button
            onClick={() => setAspect(undefined)}
            className={`px-3 py-2 rounded-full border text-xs flex items-center gap-1 ${!aspect ? 'bg-[var(--gold)] text-black border-[var(--gold)]' : 'border-white/15 text-white/80'}`}
            data-testid="crop-free"
          >
            <Maximize2 className="w-3.5 h-3.5" /> Libero
          </button>
        </div>
      </div>
    </motion.div>
  );
}
